import { Card, CardActionArea, CardProps, createStyles, makeStyles, Theme } from '@material-ui/core';
import classNames from 'classnames';
import React, { useCallback } from 'react';

import {
  CardType,
  FeedQuestionDetailsFragment,
  PlexOverviewFragment,
  ReactionType,
  useAnalyticsQuestionImpressionMutation,
} from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { LinesEllipsis } from '../../../shared/primitives/LinesEllipsis';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { useEffectOnce } from '../../details/useEffectOnce';
import { canChangeQuestionOrder } from '../../permissions';
import { logAnalytics } from '../../shared/logAnalytics';
import { CardLabels } from '../CardLabels';
import { useVisibleDelay } from '../useVisibleDelay';
import { AnswerViewContent } from './AnswerViewContent';
import { QuestionHeader } from './QuestionHeader';
import { QuestionViewFooter } from './QuestionViewFooter';
import { useDragAndDrop } from './useDragAndDrop';

type Props = {
  question: FeedQuestionDetailsFragment;
  plexData: PlexOverviewFragment;
  onSelect: (permalink: string) => void;
  selected: boolean;
  visible?: boolean;
  id?: string;
  boardId: string;
  index: number;
  onDrop: (question: FeedQuestionDetailsFragment, atIndex: number) => void;
  onDragEnd: () => void;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
};

export function QuestionView(props: Props): React.ReactElement {
  const { question, id, visible, selected, onSelect, index, onDrop, onDragEnd, moveCard, plexData, boardId } = props;
  const classes = useStyles();

  const answer = !!question.acceptedAnswer && !question.acceptedAnswer.deleted ? question.acceptedAnswer : undefined;

  const cardProps: Partial<CardProps> = selected ? { elevation: 0 } : {};

  const onVisible = useCallback((): void => {
    logAnalytics({ name: 'faq viewed', details: { plexId: plexData.id, itemId: id } });
  }, [id, plexData.id]);
  useVisibleDelay({ visible, onVisible });

  const { opacity, ref } = useDragAndDrop(
    question,
    boardId,
    index,
    canChangeQuestionOrder(plexData.currentUserRelation.state),
    onDrop,
    onDragEnd,
    moveCard
  );
  const [analyticsQuestionImpression] = useAnalyticsQuestionImpressionMutation();
  useEffectOnce(() => {
    if (!question.currentUserReactions.includes(ReactionType.AnalyticsImpression)) {
      analyticsQuestionImpression({ variables: { questionId: question.id } });
    }
  });

  return (
    <Card
      ref={ref}
      id={id}
      className={classNames(visible && classes.rootVisible, classes.root)}
      style={{ opacity }}
      square={true}
      {...cardProps}
    >
      <CardActionArea
        onClick={(): void => {
          onSelect(question.permalink);
        }}
        className={classes.cardActionArea}
      >
        <QuestionHeader
          className={classes.header}
          plexName={plexData.name}
          publishStatus={question.status}
          publishedDate={question.publishChangedAt}
          authorName={question.author?.name || 'Root'}
          authorAvatar={question.author?.picture}
          authorTitle={question.author?.jobTitle}
          showUnreadBadge={!question.notification?.isRead}
          hasAnswer={!!question.acceptedAnswer}
        />

        <div className={classes.body}>
          <div className={classes.titleContainer}>
            <PlexTypography className={classes.questionTitle} size={TextSizesEnum.NormalPlus}>
              <LinesEllipsis text={question.text} maxLine={2} />
            </PlexTypography>
            <CardLabels labels={question.labels} />
          </div>

          {answer != null ? (
            <AnswerViewContent question={question} selected={selected} showAllLines={false} answer={answer} />
          ) : (
            <AnswersInfo question={question} />
          )}
        </div>
      </CardActionArea>
      <div className={classes.footer}>
        <QuestionViewFooter plexData={plexData} question={question} />
      </div>
    </Card>
  );
}

const AnswersInfo = ({ question }: { question: FeedQuestionDetailsFragment }): React.ReactElement => {
  const classes = useStyles();
  const { answersCount, type } = question;

  return (
    <div className={classes.answersInfo}>
      {type === CardType.Faq && answersCount === 0 && 'No Answers yet.'}
      {type === CardType.Faq && answersCount > 0 && `Answers (${answersCount}) - No approved Answers`}
      {type === CardType.Generic && answersCount === 0 && 'No content yet.'}
      {type === CardType.Generic && answersCount > 0 && 'No approved content.'}
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      // flexShrink: 0,
      flexDirection: 'column',
      margin: spacings.small,
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1), 0px 0px 2px rgba(0, 0, 0, 0.06)',
      borderRadius: 6,
      backgroundColor: '#fff',
      width: 412,
      minHeight: 260,
    },
    rootVisible: {
      // backgroundColor: '#e8e8e8', // uncomment to see which items are considered visible in viewport
    },
    cardActionArea: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'baseline',
      flexGrow: 1,
    },
    header: {
      paddingTop: spacings.small,
      paddingBottom: spacings.small,
      paddingLeft: spacings.normal,
      paddingRight: spacings.normal,
    },
    body: {
      flexGrow: 1,
      width: '100%',
    },
    titleContainer: {
      display: 'inline-block',
      paddingTop: spacings.small,
      paddingLeft: spacings.normal,
      paddingRight: spacings.normal,
      paddingBottom: spacings.small,
    },
    questionTitle: {
      fontWeight: 'bold',
      color: colorPalette.dark._800,
    },
    footer: {
      display: 'flex',
      flexGrow: 0,
      flexDirection: 'column',
      justifyContent: 'flex-end',
    },
    answersInfo: {
      display: 'flex',
      flexDirection: 'column',
      padding: `${spacings.normal}px ${spacings.normal}px`,
      margin: spacings.normal,
      borderRadius: 12,
      backgroundColor: colorPalette.purpleGray._50,
      color: colorPalette.purpleGray._400,
      fontWeight: 'bold',
    },
  })
);
